import produce from 'immer';
import { pluck, values } from 'ramda';
import { push } from 'react-router-redux';
import { AnyAction } from 'redux';
import { ActionsObservable, combineEpics, ofType, StateObservable } from 'redux-observable';
import { createSelector } from 'reselect';
import { of } from 'rxjs';
import { ajax, AjaxError, AjaxResponse } from 'rxjs/ajax';
import { catchError, filter, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';

import { AscendState } from './index';
import {
  ADD_DEBT,
  ADDED_DEBT,
  ERROR_ADD_DEBT,
  EDIT_DEBT,
  EDITED_DEBT,
  ERROR_EDIT_DEBT,
  FOUND_DEBTS,
  ERROR_FOUND_DEBTS,
  FETCH_DEBT,
  FOUND_DEBT,
  ERROR_FOUND_DEBT,
  ADD_SETTLEMENT,
  ADDED_SETTLEMENT,
  ERROR_ADD_SETTLEMENT,
  ADD_SCHEDULE,
  ADDED_SCHEDULE,
  ERROR_ADD_SCHEDULE,
  ADD_PAYMENT,
  ADDED_PAYMENT,
  ERROR_ADD_PAYMENT,
  FOUND_USER_INFO,
} from './actions';
import auth from '../auth/auth';
import { lineChartColors } from '../components/shared/constants';
import config from '../config';
import { Debt } from '../models/debt';

// Reducer
export type DebtState = {
  loading: boolean;
  data: {
    [id: string]: {
      loading: boolean;
      color?: string;
      data: Debt;
      error?: AjaxError;
    };
  };
  error: AjaxError;
};

const defaultState: DebtState = {
  loading: false,
  data: {},
  error: {} as AjaxError,
};

export const debtReducer = (state = defaultState, action: AnyAction): DebtState =>
  produce(state, (draftState) => {
    switch (action.type) {
      case FETCH_DEBT: {
        draftState.data[action.id] = {
          loading: true,
          data: {} as Debt,
        };
        return;
      }

      case ADD_DEBT:
      case FOUND_USER_INFO: {
        draftState.loading = true;
        return;
      }
      case FOUND_DEBTS: {
        const debts: Debt[] = action.debts;
        draftState.loading = false;
        draftState.data = {};
        debts.forEach((debt, i) => {
          draftState.data[debt.id] = {
            loading: false,
            color: lineChartColors[i] || lineChartColors[i - 20],
            data: debt,
          };
        });
        return;
      }
      case FOUND_DEBT:
      case ADDED_SETTLEMENT:
      case ADDED_PAYMENT:
      case EDITED_DEBT:
      case ADDED_DEBT: {
        draftState.loading = false;
        const debt: Debt = action.debt;
        draftState.data[debt.id] = {
          ...draftState.data[debt.id],
          loading: false,
          data: debt,
        };
        return;
      }
      case ADDED_SCHEDULE: {
        draftState.data[action.debtId].data.balance = action.balance;
        draftState.data[action.debtId].data.schedule = action.schedule;
        return;
      }
    }
  });

// Selectors
export function getDebtState(state: AscendState) {
  return state.debt;
}
export const getDebtsLoading = createSelector(getDebtState, (debtState) => debtState.loading);
export const getAllDebts = createSelector(getDebtState, (debtState) => values(debtState.data));
export const getAllDebtsData = createSelector(getAllDebts, (debts) => pluck('data', debts));

// Epics
const getDebts$ = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    ofType(FOUND_USER_INFO),
    switchMap((action) => {
      const url = `${config.apiUrl}/debts?userId=${action.data.id}`;
      const headers = { Authorization: `Bearer ${localStorage.getItem('access_token')}` };
      const ajaxRequest = {
        url,
        headers,
        method: 'GET',
        responseType: 'json',
        withCredentials: true,
      };
      return ajax(ajaxRequest);
    }),
    map(({ response: { debts } }: { response: { debts: Debt[] } }) => ({
      type: FOUND_DEBTS,
      debts,
    })),
    catchError((error: AjaxError) => {
      // Silently handle errors for new users without debts
      // Log to console for debugging but don't show blocking errors to users
      console.log('Debts not found - user may be new without debts:', error);
      // Return empty debts array instead of error action to prevent blocking UI
      return of({ type: FOUND_DEBTS, debts: [] });
    })
  );

const getDebtById$ = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    ofType(FETCH_DEBT),
    filter(() => auth.isAuthenticated()),
    switchMap((action) => {
      const url = `${config.apiUrl}/debt/${action.id}`;
      const headers = { Authorization: `Bearer ${localStorage.getItem('access_token')}` };
      const ajaxRequest = {
        url,
        headers,
        method: 'GET',
        responseType: 'json',
        withCredentials: true,
      };
      return ajax(ajaxRequest);
    }),
    map(({ response: { debt } }: { response: { debt: Debt } }) => ({ type: FOUND_DEBT, debt })),
    catchError((error: AjaxError) => of({ type: ERROR_FOUND_DEBT, error }))
  );

const addDebt$ = (action$: ActionsObservable<AnyAction>, state$: StateObservable<AscendState>) =>
  action$.pipe(
    ofType(ADD_DEBT),
    withLatestFrom(state$),
    switchMap(([action, state]) => {
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('access_token')}`,
      };
      const userId = state.user.data.id;
      return ajax.post(`${config.apiUrl}/debt`, { ...action.debt, userId }, headers);
    }),
    map((res: AjaxResponse) => ({ type: ADDED_DEBT, debt: res.response })),
    catchError((error: AjaxError) => of({ type: ERROR_ADD_DEBT, error }))
  );

const editDebt$ = (action$: ActionsObservable<AnyAction>, state$: StateObservable<AscendState>) =>
  action$.pipe(
    ofType(EDIT_DEBT),
    withLatestFrom(state$),
    switchMap(([action, state]) => {
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('access_token')}`,
      };
      const userId = state.user.data.id;
      return ajax.put(
        `${config.apiUrl}/debt/${action.debt.id}`,
        { ...action.debt, userId },
        headers
      );
    }),
    map((res: AjaxResponse) => ({ type: EDITED_DEBT, debt: res.response })),
    catchError((error: AjaxError) => of({ type: ERROR_EDIT_DEBT, error }))
  );

const submitSettlement$ = (
  action$: ActionsObservable<AnyAction>,
  state$: StateObservable<AscendState>
) =>
  action$.pipe(
    ofType(ADD_SETTLEMENT),
    withLatestFrom(state$),
    switchMap(([action, state]) => {
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('access_token')}`,
      };
      const currentDebt = state.debt.data[action.debtId];
      const hasExistingSettlement = () => currentDebt && currentDebt.data.settlementDate;
      const settlementId = currentDebt.data.settlementId;
      const userId = state.user.data.id;
      return ajax({
        method: hasExistingSettlement() ? 'PUT' : 'POST',
        body: { ...action.settlement, id: settlementId, userId },
        url: `${config.apiUrl}/debt/${action.debtId}/settlement`,
        headers,
        withCredentials: true,
      });
    }),
    map((res: AjaxResponse) => ({ type: ADDED_SETTLEMENT, debt: res.response })),
    catchError((error: AjaxError) => of({ type: ERROR_ADD_SETTLEMENT, error }))
  );

const submitSchedule$ = (
  action$: ActionsObservable<AnyAction>,
  state$: StateObservable<AscendState>
) =>
  action$.pipe(
    ofType(ADD_SCHEDULE),
    withLatestFrom(state$),
    mergeMap(([action, state]) => {
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('access_token')}`,
      };
      const currentDebt = state.debt.data[action.debtId];
      const hasExistingSchedule = () =>
        currentDebt && currentDebt.data.schedule && currentDebt.data.schedule.length > 0;
      const userId = state.user.data.id;
      return ajax({
        method: hasExistingSchedule() ? 'PUT' : 'POST',
        body: { schedule: action.schedule, userId },
        url: `${config.apiUrl}/debt/${action.debtId}/schedule`,
        headers,
        withCredentials: true,
      }).pipe(
        map((res: AjaxResponse) => ({
          type: ADDED_SCHEDULE,
          debtId: action.debtId,
          balance: res.response.balance,
          schedule: res.response.schedule,
        })),
        catchError((error: AjaxError) => of({ type: ERROR_ADD_SCHEDULE, error }))
      );
    })
  );

const addPayment$ = (action$: ActionsObservable<AnyAction>, state$: StateObservable<AscendState>) =>
  action$.pipe(
    ofType(ADD_PAYMENT),
    withLatestFrom(state$),
    mergeMap(([action, state]) => {
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('access_token')}`,
      };
      const userId = state.user.data.id;
      return ajax
        .post(
          `${config.apiUrl}/debt/${action.debtId}/payment`,
          { ...action.payment, userId },
          headers
        )
        .pipe(
          map((res: AjaxResponse) => ({
            type: ADDED_PAYMENT,
            debt: res.response,
            debtId: action.debtId,
          })),
          catchError((error: AjaxError) => of({ type: ERROR_ADD_PAYMENT, error }))
        );
    })
  );

const routeToHome$ = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    ofType(ADDED_DEBT),
    map(() => push('/home'))
  );

const routeToDebt$ = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    ofType(ADDED_SCHEDULE),
    map((action) => push(`/debt/${action.debtId}`))
  );

export const debtEpic = combineEpics(
  getDebts$,
  getDebtById$,
  addDebt$,
  addPayment$,
  editDebt$,
  submitSchedule$,
  submitSettlement$,
  routeToHome$,
  routeToDebt$
);
